import React, { useEffect, useState, useRef, useCallback } from "react";
import { add, isWithinInterval } from "date-fns";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { ScrollPanel } from "primereact/scrollpanel";
import { Toast } from "primereact/toast";
import { Galleria } from "primereact/galleria";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { OrderService } from "./service/OrderService";

// ✨ Utility helpers you already use elsewhere
import {
  formatDateLong,
  formatDateWeekday,
  formatTime,
} from "../utils";

/**********************************************************************
 * KitchenViews.jsx  ▸  v4
 * ───────────────────────────────────────────────────────────────────
 * 1. BakersView – unchanged.
 * 2. DecoratorsView – **BIG-DISPLAY, every-detail redesign**
 *    • Regular mode  = masonry grid of cards for browsing.
 *    • Big-Display   = FULL order form on TV / large monitor.
 *      ↔ keyboard arrows & on-screen buttons → navigate orders.
 *      Auto-scales fonts for 8-10ft viewing distance.
 *********************************************************************/

/* ------------------------------------------------------------------
   Shared layout wrappers
-------------------------------------------------------------------*/
const KitchenLayout = ({ title, children, big }) => (
  <div
    style={{
      padding: big ? 0 : "1rem",
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      overflow: "hidden",
      background: big ? "#111" : "#f7f7f7",
      color: big ? "#fff" : "#000",
      fontSize: big ? "1.35rem" : undefined,
    }}
  >
    {!big && (
      <h2 style={{ margin: 0, marginBottom: "0.75rem" }}>{title}</h2>
    )}
    {children}
  </div>
);

/* ------------------------------------------------------------------
   Data-loader hook (shared)
-------------------------------------------------------------------*/
const useOrdersByDate = (dateRange) => {
  const [orders, setOrders] = useState([]);
  const toast = useRef(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const data = await OrderService.getOrdersByDateRange(
          dateRange.start,
          dateRange.end
        );
        const filtered = data.filter(
          (o) =>
            isWithinInterval(new Date(o.dueDate), {
              start: dateRange.start,
              end: dateRange.end,
            }) && o.invoiceStatus !== "CANCELED"
        );
        filtered.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        setOrders(filtered);
      } catch (err) {
        console.error("Kitchen view fetch error", err);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load orders – please refresh.",
        });
      }
    };
    fetchOrders();
  }, [dateRange]);

  return { orders, toast };
};

/* ------------------------------------------------------------------
   Formatting helpers
-------------------------------------------------------------------*/
const join = (parts) => parts.filter(Boolean).join(" ");
const cakeTierSummary = (tier) =>
  join([
    `${tier.size}\"`,
    tier.shape,
    tier.layers && tier.layers !== 3 ? `(${tier.layers}-layer)` : "",
    tier.flavor,
    tier.soak && `| soak: ${tier.soak}`,
    tier.filling && `| fill: ${tier.filling}`,
    tier.premiumFilling && `| prem-fill: ${tier.premiumFilling}`,
  ]);
const quantityLine = (qty, label) =>
  qty && qty > 0 ? `${qty} ${label}${qty > 1 ? "s" : ""}` : "";

/* In-line fancy bullets for Big mode */
const Bullet = ({ children }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
    <span style={{ fontWeight: "600" }}>•</span>
    <span style={{ flex: 1 }}>{children}</span>
  </div>
);

/* ------------------------------------------------------------------
   Bakers View – UNCHANGED
-------------------------------------------------------------------*/
export const BakersView = () => {
  const [dateRange, setDateRange] = useState({
    start: new Date(),
    end: add(new Date(), { days: 90 }),
  });
  const { orders, toast } = useOrdersByDate(dateRange);

  return (
    <KitchenLayout title="Bakers – Production Queue">
      <Toast ref={toast} />
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
        <Calendar
          showIcon
          value={dateRange.start}
          onChange={(e) => setDateRange({ ...dateRange, start: e.value })}
          placeholder="Start date"
        />
        <Calendar
          showIcon
          value={dateRange.end}
          onChange={(e) => setDateRange({ ...dateRange, end: e.value })}
          placeholder="End date"
        />
        <Button
          label="Today"
          onClick={() =>
            setDateRange({ start: new Date(), end: add(new Date(), { days: 90 }) })
          }
        />
        <Button label="Print" icon="pi pi-print" onClick={() => window.print()} />
      </div>

      <ScrollPanel style={{ height: "calc(100vh - 180px)" }}>
        {/* concise panels here unchanged */}
        {orders.map((order) => (
          <Panel
            header={`${formatDateWeekday(order.dueDate)}, ${formatDateLong(
              order.dueDate
            )}  •  ${order.orderID ? order.orderID + " – " : ""}${order.customer.name}`}
            key={order._id}
            toggleable
            collapsed
          >
            {/* … */}
          </Panel>
        ))}
      </ScrollPanel>
    </KitchenLayout>
  );
};

/* ------------------------------------------------------------------
   Helpers to render every product line (decorator-centric)
-------------------------------------------------------------------*/
const renderCupcake = (c) =>
  join([
    quantityLine(c.quantity, "cupcake"),
    c.flavor,
    c.filling && `| fill: ${c.filling}`,
    c.finalIcing && `| icing: ${c.finalIcing}${c.finalIcingColor ? ` (${c.finalIcingColor})` : ""}`,
    c.decoratingNotes && `» ${c.decoratingNotes}`,
  ]);

const renderCookie = (ck) =>
  join([
    quantityLine(ck.quantity, "cookie"),
    ck.shape,
    ck.flavor,
    ck.icing && `| icing: ${ck.icing}${ck.icingColor ? ` (${ck.icingColor})` : ""}`,
    ck.decoratingNotes && `» ${ck.decoratingNotes}`,
  ]);

const renderCakePop = (p) =>
  join([
    quantityLine(p.quantity, "cake pop"),
    p.shape,
    p.type,
    p.flavor,
    p.chocolateCoating && `| coat: ${p.chocolateCoating}${p.coatingColor ? ` (${p.coatingColor})` : ""}`,
    p.orientation && `| orientation: ${p.orientation}`,
    p.decoratingNotes && `» ${p.decoratingNotes}`,
  ]);

const renderGeneralItem = (gi) =>
  join([
    quantityLine(gi.quantity, gi.item || "item"),
    gi.flavor && gi.flavor,
    gi.description && `– ${gi.description}`,
    gi.flavorNotes && `(${gi.flavorNotes})`,
    gi.decoratingNotes && `» ${gi.decoratingNotes}`,
  ]);

/* ------------------------------------------------------------------
   Image helpers
-------------------------------------------------------------------*/
const InspirationCarousel = ({ photos }) => {
  if (!photos || photos.length === 0) return null;
  return (
    <Galleria
      value={photos}
      numVisible={5}
      showThumbnails
      circular
      item={(photo) => (
        <img src={photo.url} alt={photo.tag} style={{ width: "100%" }} />
      )}
      thumbnail={(photo) => (
        <img
          src={photo.url}
          alt={photo.tag}
          style={{ width: 100, height: 70, objectFit: "cover" }}
        />
      )}
      style={{ maxWidth: "100%", marginBottom: "0.75rem" }}
    />
  );
};

/* ------------------------------------------------------------------
   OrderCard – SINGLE source of truth (used in grid & big view)
-------------------------------------------------------------------*/
const OrderCard = ({ order, big }) => {
  if (!order) return null;
  const inspPhotos = (order.photos || []).filter((p) => p.tag === "Inspiration");

  return (
    <Card
      className="order-card"
      style={{
        background: big ? "#1d1d1d" : "#fff",
        color: big ? "#fff" : "#000",
        borderRadius: 12,
        boxShadow: big ? "none" : "0 2px 10px rgba(0,0,0,0.15)",
        overflow: "hidden",
        width: "100%",
      }}
      title={`${formatDateWeekday(order.dueDate)} – ${order.customer.name}`}
      subTitle={`${order.orderID ? order.orderID + " • " : ""}${order.name}`}
    >
      {/* Photos */}
      <InspirationCarousel photos={inspPhotos} />

      {/* Meta tags */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem" }}>
        <Tag
          severity="info"
          value={`${order.type} @ ${formatTime(order.scheduledTime)}`}
        />
        <Tag
          severity={
            order.invoiceStatus === "PAID" || order.invoiceStatus === "COMP"
              ? "success"
              : order.invoiceStatus === "PARTIALLY_PAID"
              ? "warning"
              : "danger"
          }
          value={order.invoiceStatus}
        />
      </div>

      {/* Detailed itemisation – decorators need EVERYTHING */}
      {order.orderItems.map((oi, idx) => (
        <div key={idx} style={{ lineHeight: 1.45 }}>
          {/* CAKES */}
          {oi.cakes.map((cake, i) => (
            <div key={`cake-${i}`} style={{ marginBottom: "0.65rem" }}>
              <strong style={{ textDecoration: "underline" }}>
                Cake {oi.cakes.length > 1 ? i + 1 : ""}
              </strong>
              {cake.tiers.map((tier, tIdx) => (
                <Bullet key={`tier-${tIdx}`}>{cakeTierSummary(tier)}</Bullet>
              ))}
              {cake.cakeBoardInscription && (
                <Bullet>
                  <em>Cakeboard: “{cake.cakeBoardInscription}”</em>
                </Bullet>
              )}
              {cake.cakeBoardNote && <Bullet>{cake.cakeBoardNote}</Bullet>}
              {cake.decoratingNotes && <Bullet>{cake.decoratingNotes}</Bullet>}
              {cake.toppers.length > 0 && (
                <Bullet>
                  <strong>Topper:</strong> {cake.toppers.map((t) => t.description).join(", ")}
                </Bullet>
              )}
              {cake.decoratingItems.length > 0 && (
                <Bullet>
                  <strong>Decorating Items:</strong> {cake.decoratingItems.map((d) => d.description).join(", ")}
                </Bullet>
              )}
            </div>
          ))}

          {/* CUPCAKES */}
          {oi.cupcakes.map((cup, ci) => (
            <Bullet key={`cup-${ci}`}>{renderCupcake(cup)}</Bullet>
          ))}

          {/* COOKIES */}
          {oi.cookies.map((ck, cki) => (
            <Bullet key={`cookie-${cki}`}>{renderCookie(ck)}</Bullet>
          ))}

          {/* CAKE POPS */}
          {oi.cakePops.map((p, pi) => (
            <Bullet key={`pop-${pi}`}>{renderCakePop(p)}</Bullet>
          ))}

          {/* GENERAL ITEMS */}
          {oi.generalItems.map((gi, giIdx) => (
            <Bullet key={`gi-${giIdx}`}>{renderGeneralItem(gi)}</Bullet>
          ))}

          {/* Order-level note */}
          {order.orderNote && (
            <div style={{ marginTop: "0.5rem", fontStyle: "italic" }}>
              <strong>Note:</strong> {order.orderNote}
            </div>
          )}
        </div>
      ))}
    </Card>
  );
};

/* ------------------------------------------------------------------
   DecoratorsView – GRID + BIG-DISPLAY carousel
-------------------------------------------------------------------*/
export const DecoratorsView = () => {
  const query = new URLSearchParams(window.location.search);
  const isBig = query.get("mode") === "big";

  const [dateRange, setDateRange] = useState({
    start: new Date(),
    end: add(new Date(), { days: 90 }),
  });

  const { orders, toast } = useOrdersByDate(dateRange);

  /* Big-mode index + navigation */
  const [idx, setIdx] = useState(0);
  const total = orders.length;
  const go = useCallback(
    (delta) => {
      setIdx((prev) => {
        if (total === 0) return 0;
        return (prev + delta + total) % total;
      });
    },
    [total]
  );

  /* Keyboard arrows in Big mode */
  useEffect(() => {
    if (!isBig) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isBig, go]);

  /* Pop-out big window */
  const openBigWindow = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("mode", "big");
    window.open(url.toString(), "_blank", "noopener,noreferrer,width=1400,height=900");
  };

  /* ----------------------------------------------------------------*/
  return (
    <KitchenLayout title="Decorators – Finishing Queue" big={isBig}>
      <Toast ref={toast} />

      {/* CONTROL BAR (hidden on TV big mode) */}
      {!isBig && (
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <Calendar
            showIcon
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.value })}
          />
          <Calendar
            showIcon
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.value })}
          />
          <Button label="Today" onClick={() => setDateRange({ start: new Date(), end: add(new Date(), { days: 90 }) })} />
          <Button label="Print" icon="pi pi-print" onClick={() => window.print()} />
          <Button label="Big Display" icon="pi pi-external-link" className="p-button-help p-ml-auto" onClick={openBigWindow} />
        </div>
      )}

      {/* REGULAR GRID MODE */}
      {!isBig && (
        <ScrollPanel style={{ height: "calc(100vh - 180px)" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(380px,1fr))",
              gap: "1rem",
            }}
          >
            {orders.map((o) => (
              <OrderCard key={o._id} order={o} />
            ))}
          </div>
        </ScrollPanel>
      )}

      {/* BIG SINGLE-ORDER MODE */}
      {isBig && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <div
            style={{
              padding: "0.5rem 1rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#000",
              color: "#fff",
            }}
          >
            <Button
              icon="pi pi-angle-left"
              className="p-button-text p-button-lg"
              style={{ color: "white" }}
              onClick={() => go(-1)}
              disabled={total === 0}
            />
            <div style={{ fontSize: "1.1rem" }}>{total > 0 ? `${idx + 1} / ${total}` : "No orders"}</div>
            <Button
              icon="pi pi-angle-right"
              className="p-button-text p-button-lg"
              style={{ color: "white" }}
              onClick={() => go(1)}
              disabled={total === 0}
            />
          </div>

          <div style={{ flex: 1, overflow: "auto", padding: "1rem" }}>
            {total > 0 && <OrderCard order={orders[idx]} big />}
          </div>
        </div>
      )}
    </KitchenLayout>
  );
};

/**********************************************************************
 * Barrel export – import { BakersView, DecoratorsView } from …
 *********************************************************************/
export default { BakersView, DecoratorsView };